// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { RxDatabase } from 'rxdb';
import { QueryOperator } from '../operators/QueryOperator';
import { MutationOperator } from '../operators/MutationOperator';
import type { QueryOptions } from '../operators/types';
import type { TaskModel } from '../../models/Task';
import { createId } from '../../index';

/**
 * Task query filters
 */
export interface TaskFilters {
  /** Organization ID (required for multi-tenancy) */
  orgId?: string;
  /** Filter by user ID */
  userId?: string;
  /** Filter by workspace ID */
  workspaceId?: string;
  /** Filter by project ID */
  projectId?: string;
  /** Filter by milestone ID */
  milestoneId?: string;
  /** Filter by parent task ID for subtasks */
  parentTaskId?: string;
  /** Exclude subtasks (tasks with parentTaskId) - set to true to only show top-level tasks */
  excludeSubtasks?: boolean;
  /** Filter by task status */
  status?: TaskModel['status'];
  /** Filter by completion state */
  completed?: boolean;
  /** Filter by archived state (default: false) */
  archived?: boolean;
}

/**
 * Task creation input
 */
export interface CreateTaskInput {
  /** Task title */
  title: string;
  /** Task description */
  description: string;
  /** Organization ID */
  orgId: string;
  /** User ID who created the task */
  userId: string;
  /** Parent workspace ID (required - all tasks belong to a workspace) */
  workspaceId: string;
  /** Optional parent project ID (tasks can exist at workspace level) */
  projectId?: string;
  /** Optional parent milestone ID (tasks can exist at project level) */
  milestoneId?: string;
  /** Optional parent task ID for creating subtasks */
  parentTaskId?: string;
  /** Optional task status (default: 'todo') */
  status?: TaskModel['status'];
  /** Optional scheduled start date */
  startAt?: string;
  /** Optional due date */
  dueAt?: string;
  /** Optional task type (default: 'todo') */
  type?: TaskModel['type'];
  /** Optional category ID */
  categoryId?: string;
  /** Optional tag IDs */
  tagIds?: string[];
  /** Optional time budget in hours */
  timeBudgetHours?: number;
  /** Optional start date */
  startedAt?: string;
  /** Optional completion date */
  completedAt?: string;
  /** Optional recurrence flag */
  isRecurring?: boolean;
  /** Optional iCal RRULE recurrence rule string */
  recurrenceRule?: string;
  /** Optional parent recurrence ID */
  parentRecurrenceId?: string;
  /** Optional recurrence instance date */
  recurrenceInstanceDate?: string;
  /** Optional priority (0-4, default: 0) */
  priority?: number;
  /** Optional assignee team member ID */
  assigneeId?: string;
  /** Optional sort order */
  sortOrder?: number;
}

/**
 * Task update input
 */
export interface UpdateTaskInput {
  /** Task ID to update */
  id: string;
  /** Partial task data to update */
  updates: Partial<TaskModel>;
}

/**
 * Framework-agnostic task operations
 *
 * Provides query and mutation operators for tasks that work in any environment.
 * Use this directly in Node.js scripts or wrap with framework hooks (React, Vue, etc.).
 *
 * @example
 * ```typescript
 * // Node.js script usage
 * const taskOps = new TaskOperator(db);
 * const query = taskOps.query({ orgId: 'org_123', status: 'in_progress' });
 * const tasks = await query.exec();
 *
 * // Create task
 * const mutation = taskOps.createMutation();
 * const newTask = await mutation.execute({
 *   title: 'New Task',
 *   description: 'Task description',
 *   orgId: 'org_123',
 *   userId: 'user_456',
 *   projectId: 'proj_789',
 *   milestoneId: 'mile_101'
 * });
 * ```
 */
export class TaskOperator {
  constructor(private db: RxDatabase) {}

  /**
   * Create a query operator for tasks
   *
   * Returns a QueryOperator that can be used imperatively (exec) or reactively (observable).
   *
   * @param filters - Task filters to apply
   * @param options - Optional query options (sort, limit, skip)
   * @returns QueryOperator instance for tasks
   */
  query(
    filters: TaskFilters,
    options?: {
      sort?: string;
      limit?: number;
      skip?: number;
    }
  ): QueryOperator<TaskModel> {
    const collection = this.db.collections.tasks;

    // Build selector - orgId is optional to support "View All" mode across organizations
    // Use $ne: true instead of $eq: false to match documents where archived is false, undefined, or missing
    const selector: any = {
      archived: filters.archived === true ? { $eq: true } : { $ne: true },
    };

    // Only add orgId filter if provided (allows cross-org queries when not specified)
    if (filters.orgId) {
      selector.orgId = { $eq: filters.orgId };
    }

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId };
    }

    if (filters.milestoneId) {
      selector.milestoneId = { $eq: filters.milestoneId };
    } else if (filters.projectId) {
      selector.projectId = { $eq: filters.projectId };
    }

    if (filters.parentTaskId) {
      selector.parentTaskId = { $eq: filters.parentTaskId };
    } else if (filters.excludeSubtasks) {
      // Exclude tasks that have a parentTaskId (i.e., subtasks)
      console.log('[TaskOperator] Applying excludeSubtasks filter');
      selector.parentTaskId = { $exists: false };
    }

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    if (filters.status) {
      selector.status = { $eq: filters.status };
    }

    // Use $ne: true for completed filter to match documents where completed is false, undefined, or missing
    if (filters.completed !== undefined) {
      selector.completed = filters.completed === true ? { $eq: true } : { $ne: true };
    }

    const queryOptions: QueryOptions<TaskModel> = {
      selector,
      sort: options?.sort ? [{ [options.sort]: 'desc' }] : [{ createdAt: 'desc' }],
      limit: options?.limit,
      skip: options?.skip,
    };

    console.log('[TaskOperator] Query selector:', JSON.stringify(selector, null, 2));
    console.log('[TaskOperator] Filters:', JSON.stringify(filters, null, 2));

    return new QueryOperator<TaskModel>(collection, queryOptions);
  }

  /**
   * Find a single task by ID
   *
   * @param taskId - Task ID to find
   * @returns Promise resolving to task or null if not found
   */
  async findOne(taskId: string): Promise<TaskModel | null> {
    const doc = await this.db.collections.tasks.findOne(taskId).exec();
    return doc ? (doc.toJSON() as TaskModel) : null;
  }

  /**
   * Count tasks matching the given filters
   *
   * @param filters - Task filters to apply
   * @returns Promise resolving to count of matching tasks
   */
  async count(filters: TaskFilters): Promise<number> {
    const collection = this.db.collections.tasks;

    // Build selector - orgId is optional to support "View All" mode across organizations
    // Use $ne: true instead of $eq: false to match documents where archived is false, undefined, or missing
    const selector: any = {
      archived: filters.archived === true ? { $eq: true } : { $ne: true },
    };

    // Only add orgId filter if provided
    if (filters.orgId) {
      selector.orgId = { $eq: filters.orgId };
    }

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId };
    }

    if (filters.milestoneId) {
      selector.milestoneId = { $eq: filters.milestoneId };
    } else if (filters.projectId) {
      selector.projectId = { $eq: filters.projectId };
    }

    if (filters.parentTaskId) {
      selector.parentTaskId = { $eq: filters.parentTaskId };
    }

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    if (filters.status) {
      selector.status = { $eq: filters.status };
    }

    // Use $ne: true for completed filter to match documents where completed is false, undefined, or missing
    if (filters.completed !== undefined) {
      selector.completed = filters.completed === true ? { $eq: true } : { $ne: true };
    }

    return await collection.count({ selector }).exec();
  }

  /**
   * Create a mutation operator for creating tasks
   *
   * @returns MutationOperator for task creation
   */
  createMutation(): MutationOperator<TaskModel, CreateTaskInput> {
    return new MutationOperator<TaskModel, CreateTaskInput>(this.db.collections.tasks, {
      mutationFn: async (collection, input) => {
        const now = new Date().toISOString();
        const task: TaskModel = {
          id: createId('task'),
          orgId: input.orgId,
          userId: input.userId,
          workspaceId: input.workspaceId,
          title: input.title,
          description: input.description,
          name: input.title.toLowerCase().replace(/\s+/g, '-'),
          status: input.status || 'todo',
          type: input.type || 'todo',
          completed: false,
          archived: false,
          priority: input.priority ?? 0,
          assigneeId: input.assigneeId ?? '',
          sortOrder: input.sortOrder ?? 0,
          version: 1,
          projectId: input.projectId ?? '',
          milestoneId: input.milestoneId ?? '',
          parentTaskId: input.parentTaskId ?? '',
          createdAt: now,
          updatedAt: now,
          ...(input.startAt && { startAt: input.startAt }),
          ...(input.dueAt && { dueAt: input.dueAt }),
          ...(input.categoryId && { categoryId: input.categoryId }),
          ...(input.tagIds && { tagIds: input.tagIds }),
          ...(input.timeBudgetHours && { timeBudgetHours: input.timeBudgetHours }),
          ...(input.startedAt && { startedAt: input.startedAt }),
          ...(input.completedAt && { completedAt: input.completedAt }),
          ...(input.isRecurring !== undefined && { isRecurring: input.isRecurring }),
          ...(input.recurrenceRule && { recurrenceRule: input.recurrenceRule }),
          ...(input.parentRecurrenceId && { parentRecurrenceId: input.parentRecurrenceId }),
          ...(input.recurrenceInstanceDate && { recurrenceInstanceDate: input.recurrenceInstanceDate }),
        };

        await collection.insert(task);
        return task;
      },
    });
  }

  /**
   * Create a mutation operator for updating tasks
   *
   * @returns MutationOperator for task updates
   */
  updateMutation(): MutationOperator<TaskModel, UpdateTaskInput> {
    return new MutationOperator<TaskModel, UpdateTaskInput>(this.db.collections.tasks, {
      mutationFn: async (collection, { id, updates }) => {
        const doc = await collection.findOne(id).exec();
        if (!doc) {
          throw new Error(`Task ${id} not found`);
        }

        await doc.update({
          $set: {
            ...updates,
            updatedAt: new Date().toISOString(),
          },
        });

        return doc.toJSON() as TaskModel;
      },
    });
  }

  /**
   * Create a mutation operator for deleting tasks (soft delete)
   *
   * Marks task as archived rather than physically deleting it.
   *
   * @returns MutationOperator for task deletion
   */
  deleteMutation(): MutationOperator<void, string> {
    return new MutationOperator<void, string>(this.db.collections.tasks, {
      mutationFn: async (collection, taskId) => {
        const doc = await collection.findOne(taskId).exec();
        if (!doc) {
          throw new Error(`Task ${taskId} not found`);
        }

        await doc.update({
          $set: {
            archived: true,
            updatedAt: new Date().toISOString(),
          },
        });
      },
    });
  }

  /**
   * Create a mutation operator for completing tasks
   *
   * Sets task status to 'done' and marks as completed.
   *
   * @returns MutationOperator for task completion
   */
  completeMutation(): MutationOperator<TaskModel, string> {
    return new MutationOperator<TaskModel, string>(this.db.collections.tasks, {
      mutationFn: async (collection, taskId) => {
        const doc = await collection.findOne(taskId).exec();
        if (!doc) {
          throw new Error(`Task ${taskId} not found`);
        }

        const now = new Date().toISOString();
        await doc.update({
          $set: {
            status: 'done',
            completed: true,
            completedAt: now,
            updatedAt: now,
          },
        });

        return doc.toJSON() as TaskModel;
      },
    });
  }
}
